"use client";

import type { TenantIdentity } from "@/config/tenants";
import { useAppLocale } from "@/features/localization/app-locale-provider";
import {
  privacyCopy,
  type PrivacySection,
} from "@/features/legal/content/privacy-copy";
import { LegalPageHeader } from "@/features/legal/components/legal-page-header";

export function PrivacyPageContent({ tenant }: Readonly<{ tenant: TenantIdentity }>) {
  const { locale } = useAppLocale();
  const copy = privacyCopy[locale];

  return (
    <main className="bg-muted/40 px-4 py-10 sm:px-6 sm:py-14">
      <article className="mx-auto max-w-5xl rounded-2xl border border-border bg-card px-5 py-7 text-card-foreground shadow-sm sm:px-10 sm:py-10 lg:px-14">
        <LegalPageHeader tenant={tenant} />

        <div className="py-10 sm:py-12">
          <p className="text-sm font-bold uppercase tracking-wider text-primary">
            {copy.version}
          </p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
            {copy.title}
          </h1>
          <p className="mt-7 leading-7 text-muted-foreground">
            {copy.introduction}
          </p>
          <p className="mt-5 border-l-4 border-primary px-5 py-3 leading-7 text-muted-foreground">
            {copy.precedence}
          </p>
        </div>

        <div>
          {copy.sections.map((section) => (
            <PrivacySectionView key={section.title} section={section} />
          ))}
        </div>
      </article>
    </main>
  );
}

function PrivacySectionView({ section }: { section: PrivacySection }) {
  return (
    <section className="border-t border-border py-8">
      <h2 className="text-2xl font-bold tracking-tight">{section.title}</h2>
      {section.intro ? (
        <p className="mt-4 leading-7 text-muted-foreground">{section.intro}</p>
      ) : null}
      {section.paragraphs?.map((paragraph) => (
        <p className="mt-4 leading-7 text-muted-foreground" key={paragraph}>
          {paragraph}
        </p>
      ))}
      {section.items ? (
        <ul className="mt-5 grid gap-3">
          {section.items.map((item) => (
            <li
              className="rounded-xl bg-muted/70 px-5 py-4 leading-7 text-muted-foreground"
              key={item.label}
            >
              <strong className="text-foreground">{item.label}:</strong>{" "}
              {item.text}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
